import {Form, Formik, FormikProps} from 'formik';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {settingsApi} from 'api';
import {
    Button, Card, DataWrapper, ExternalIntegrationPurposeSelect, InputFormik, PasswordInputFormik,
    PredefinedNotifierType, ToggleFormik, UnsavedChangesLeavePrompt
} from 'components';
import {UserContext} from 'context';
import {AiProvider, ExternalIntegrationPurpose, ValidationCode} from 'enums';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {InternalLiveAgentSettings} from 'types';
import {apiUtils, dataUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.INTERNAL_LIVE_AGENT;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

type FormTemplateProps = {
    loading: boolean;
    maskedAgentKey: string;
} & FormikProps<InternalLiveAgentSettings>;

const FIELDS = {
    ENABLED: 'enabled',
    BASE_URL: 'baseUrl',
    AGENT_KEY: 'agentKey',
    AGENT_ID: 'agentId',
};

const FormTemplate: React.FC<FormTemplateProps> = ({
    loading, values, initialValues, submitForm, isValid, maskedAgentKey
}) => {
    const {t} = useTranslation();
    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [initialValues, values]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Card>
                    <div className='tw-max-w-104'>
                        <ToggleFormik
                            name={FIELDS.ENABLED}
                            label={t(T_KEYS.LABELS.ENABLED)}
                            className='tw-my-4'
                        />
                        <InputFormik
                            name={FIELDS.BASE_URL}
                            label={t(T_KEYS.LABELS.BASE_URL)}
                            className='tw-w-full'
                            required={values.enabled}
                            spacing='top'
                        />
                        <PasswordInputFormik
                            name={FIELDS.AGENT_KEY}
                            label={t(T_KEYS.LABELS.AGENT_KEY)}
                            className='tw-w-full tw-mt-4'
                            spacing='bottom'
                            required={values.enabled && !maskedAgentKey}
                            autoComplete='one-time-code'
                            description={maskedAgentKey}
                        />
                        <InputFormik
                            name={FIELDS.AGENT_ID}
                            label={t(T_KEYS.LABELS.AGENT_ID)}
                            className='tw-w-full tw-mt-4'
                            spacing='bottom'
                            required={values.enabled}
                        />

                        <div className='tw-text-right tw-mt-controls'>
                            <Button
                                color='primary'
                                disabled={hasNoChanges || !isValid}
                                loading={loading}
                                onClick={submitForm}
                            >
                                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                            </Button>
                        </div>
                    </div>
                </Card>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

export const InternalLiveAgentSettingsPage: React.FC = () => {
    const {settings: {providerPurposes}} = useContext(UserContext);
    const purposes = useMemo(() => providerPurposes[AiProvider.InternalLiveAgent] || [], [providerPurposes]);
    const [integrationPurpose, setIntegrationPurpose] = useState<ExternalIntegrationPurpose>(ExternalIntegrationPurpose.ClientTextChat);
    const [maskedAgentKey, setMaskedAgentKey] = useState('');
    const [initialValues, setInitialValues] = useState<InternalLiveAgentSettings>({
        enabled: false,
        baseUrl: '',
        agentKey: '',
        agentId: ''
    });

    const {
        send: fetchSettingsFn, loading: fetchLoading, data: settings
    } = useApiData(settingsApi.getInternalLiveAgent, {notificationType: PredefinedNotifierType.FetchError});

    const fetchSettings = useCallback(() => {
        fetchSettingsFn(integrationPurpose);
    }, [integrationPurpose, fetchSettingsFn]);

    const {send: updateSettings, loading: updateLoading} = useApiData(settingsApi.updateInternalLiveAgent,
        {onSuccess: fetchSettings, notificationType: PredefinedNotifierType.Save});

    const {t} = useTranslation();
    const validationSchema = useMemo(() => Yup.object().shape({
        [FIELDS.ENABLED]: Yup.boolean(),
        [FIELDS.BASE_URL]: Yup.string()
            .when(FIELDS.ENABLED, {
                is: true,
                then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
            }),
        [FIELDS.AGENT_KEY]: Yup.string()
            .when(FIELDS.ENABLED, {
                is: true,
                then: (schema) => maskedAgentKey ? schema :
                    schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
            }),
        [FIELDS.AGENT_ID]: Yup.string()
            .when(FIELDS.ENABLED, {
                is: true,
                then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
            })
    }), [t, maskedAgentKey]);

    const onSubmit = useCallback((values: InternalLiveAgentSettings) => {
        updateSettings(integrationPurpose, values);
    }, [integrationPurpose, updateSettings]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    useEffect(() => {
        setMaskedAgentKey(settings?.agentKey);
        setInitialValues({
            enabled: !!settings?.enabled,
            baseUrl: settings?.baseUrl || '',
            agentKey: '',
            agentId: settings?.agentId || ''
        });
    }, [settings]);

    return (
        <Fragment>
            <ExternalIntegrationPurposeSelect
                value={integrationPurpose}
                purposes={purposes}
                className='tw-mb-4 tw-max-w-104'
                onChange={setIntegrationPurpose}
            />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={onSubmit}
            >
                {(props) => (
                    <FormTemplate
                        {...props}
                        maskedAgentKey={maskedAgentKey}
                        loading={fetchLoading || updateLoading}
                    />
                )}
            </Formik>
        </Fragment>
    );
};
